/**
 * Coverage rows in stored audit revisions.
 *
 * The certification producer used to project policies it never ran, and
 * policies it could not decide, into `rule_compliance_assessments`. Those rows
 * carry no `rule_title` and a `reason` of `NOT_RUN:<code>` or
 * `UNSUPPORTED:<code>`, so they render as cards titled with a raw policy id and
 * they distort the stored `compliance_percentage`.
 *
 * The producer no longer emits them. Revisions written before that fix still
 * hold them, so the sidebar filters them out on read. Remove this module once
 * no reachable revision predates the producer fix.
 */

import type { RuleComplianceAssessment } from '../types';

const COVERAGE_REASON_PREFIXES = ['NOT_RUN:', 'UNSUPPORTED:'];

/** True when the row reports policy coverage rather than a documentation finding. */
export function isCoverageRow(assessment: RuleComplianceAssessment): boolean {
  const reason = assessment.reason ?? '';
  return COVERAGE_REASON_PREFIXES.some(prefix => reason.startsWith(prefix));
}

/** The rows that state something about this note. */
export function selectFindingRows(
  assessments: RuleComplianceAssessment[] | null | undefined,
): RuleComplianceAssessment[] {
  if (!assessments) return [];
  return assessments.filter(assessment => !isCoverageRow(assessment));
}

/**
 * Compliance percentage over finding rows only.
 *
 * Returns null when no finding row exists, so callers can fall back to the
 * stored value rather than show a zero.
 */
export function complianceFromFindingRows(
  assessments: RuleComplianceAssessment[] | null | undefined,
): number | null {
  const rows = selectFindingRows(assessments);
  if (rows.length === 0) return null;
  const compliant = rows.filter(row => row.compliant === 'yes').length;
  return (compliant / rows.length) * 100;
}
